import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Shared codebook-cache factory.
 *
 * One place that gets the stale-while-revalidate contract right, so every
 * cached dropdown (machines, processes, subprocesses, countries…) inherits the
 * SAME correct behaviour instead of re-implementing `_cache`/`_inflight` and
 * forgetting to revalidate.
 *
 * What each cache guarantees:
 *  - Instant paint from an in-memory cache.
 *  - SWR: refetch on every mount.
 *  - Subscriber broadcast: a single refetch pushes the fresh list to ALL mounted
 *    consumers — critical under keep-alive open-tabs, where a screen (e.g. /os)
 *    stays mounted and never remounts to trigger its own refetch.
 *  - Auto-invalidate on write: a successful POST/PUT/PATCH/DELETE to the cache's
 *    endpoint invalidates + refetches it, so editing a codebook in one screen
 *    refreshes every open dropdown with no page reload.
 */

export interface CodebookCache<T> {
  /** Hook: subscribe to this codebook's options (SWR on mount, live updates after). */
  useOptions: () => T[];
  /** Force a fetch now and broadcast to all mounted consumers. Deduped by the in-flight guard. */
  refetch: () => Promise<void>;
  /** Drop the cached list so the next mount fetches fresh. */
  invalidate: () => void;
  /** Seed the cache directly (e.g. from an already-loaded payload) and broadcast. */
  prime: (list: T[]) => void;
  /**
   * Synchronous read of the cached list, or null when nothing is cached yet.
   * For non-React callers that need to resolve an id → label without a fetch.
   * Never triggers a request: a null here means "not loaded", not "empty codebook".
   */
  peek: () => T[] | null;
}

// ─── Auto-invalidate interceptor (installed once, shared by all caches) ───────

interface RegistryEntry {
  matches: (url: string) => boolean;
  invalidate: () => void;
  refetch: () => Promise<void>;
}

const _registry: RegistryEntry[] = [];
let _interceptorInstalled = false;

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function installInvalidateInterceptor() {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  axios.interceptors.response.use((response) => {
    const method = (response.config.method || 'get').toLowerCase();
    if (method === 'post' || method === 'put' || method === 'patch' || method === 'delete') {
      const url = response.config.url || '';
      _registry.forEach((entry) => {
        if (entry.matches(url)) {
          entry.invalidate();
          // Proactively broadcast fresh data to every mounted consumer.
          entry.refetch();
        }
      });
    }
    return response;
  });
}

/**
 * Run `onWrite` after every successful POST/PUT/PATCH/DELETE to `path` (or a sub-path), from
 * any screen. For list screens that keep their own rows instead of a cache (e.g. /machines):
 * under keep-alive tabs they never remount, so an Excel import done from the admin console
 * stayed invisible until F5. Returns the unsubscribe — call it from the effect cleanup.
 */
export function onApiWrite(path: string, onWrite: () => void): () => void {
  const boundary = new RegExp(escapeRegex(path) + '(/|\\?|#|$)');
  const entry: RegistryEntry = {
    matches: (url) => boundary.test(url),
    invalidate: () => {},
    refetch: async () => onWrite(),
  };
  _registry.push(entry);
  installInvalidateInterceptor();
  return () => {
    const index = _registry.indexOf(entry);
    if (index >= 0) _registry.splice(index, 1);
  };
}

// ─── Factory ─────────────────────────────────────────────────────────────────

export function createCodebookCache<T>(opts: {
  /** Relative API path, e.g. '/api/machines'. Writes to this path auto-invalidate the cache. */
  endpoint: string;
  /** Query params for the list GET, e.g. { status: 'active' }. */
  params?: Record<string, unknown>;
  /** Extract the array from the response body. Defaults to `data` or `data.items`. */
  select?: (data: any) => T[];
  /**
   * Extra path(s) whose writes must also invalidate this cache. Needed when the read
   * endpoint is a sub-route of the collection — e.g. reading '/api/unit-measurments/active'
   * while POST/PUT/DELETE go to '/api/unit-measurments', which the endpoint's own
   * path-boundary matcher would never match.
   */
  invalidateOn?: string | string[];
}): CodebookCache<T> {
  const { endpoint, params = {}, select, invalidateOn } = opts;

  let cache: T[] | null = null;
  const inflight = { promise: null as Promise<void> | null };
  const subscribers = new Set<(list: T[]) => void>();

  const refetch = (): Promise<void> => {
    if (inflight.promise) return inflight.promise;
    const token = localStorage.getItem('token');
    inflight.promise = axios
      .get(endpoint, {
        headers: token ? { 'x-auth-token': token } : {},
        params,
      })
      .then((r) => {
        const list: T[] = select
          ? select(r.data)
          : Array.isArray(r.data)
            ? r.data
            : r.data?.items || [];
        cache = list;
        subscribers.forEach((fn) => fn(list));
      })
      .catch(() => {})
      .finally(() => { inflight.promise = null; }) as unknown as Promise<void>;
    return inflight.promise;
  };

  const invalidate = () => { cache = null; };

  const prime = (list: T[]) => {
    cache = list;
    subscribers.forEach((fn) => fn(list));
  };

  const useOptions = (): T[] => {
    const [options, setOptions] = useState<T[]>(cache || []);
    useEffect(() => {
      subscribers.add(setOptions);
      // Keep local state in sync if the cache was primed before this mount.
      if (cache) setOptions(cache);
      refetch();
      return () => { subscribers.delete(setOptions); };
    }, []);
    return options;
  };

  // Register for auto-invalidation on writes to this endpoint (path-boundary match
  // so '/api/machines' does not accidentally match '/api/machineTypes').
  const watched = [endpoint, ...(Array.isArray(invalidateOn) ? invalidateOn : invalidateOn ? [invalidateOn] : [])];
  watched.forEach((path) => {
    const boundary = new RegExp(escapeRegex(path) + '(/|\\?|#|$)');
    _registry.push({ matches: (url) => boundary.test(url), invalidate, refetch });
  });
  installInvalidateInterceptor();

  return { useOptions, refetch, invalidate, prime, peek: () => cache };
}
